import { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

interface UseSortAndFilterOptions<T, SortKey extends string> {
  rows: T[];
  matchesFilter: (row: T, normalizedFilter: string) => boolean;
  comparators: Record<SortKey, (a: T, b: T) => number>;
}

// Shared client-side sort/filter for the LidarClearance tab tables - all
// three tabs list a small, fully-loaded array (no server paging), so sorting
// and filtering in memory keeps every tab's table logic identical.
export function useSortAndFilter<T, SortKey extends string>({
  rows,
  matchesFilter,
  comparators,
}: UseSortAndFilterOptions<T, SortKey>) {
  const [filterText, setFilterText] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const visibleRows = useMemo(() => {
    const normalizedFilter = filterText.trim().toLowerCase();
    // Always a fresh array (even unfiltered) so sorting below never mutates
    // the caller's `rows` state.
    const filtered = normalizedFilter
      ? rows.filter((row) => matchesFilter(row, normalizedFilter))
      : [...rows];

    if (!sortKey) return filtered;

    const comparator = comparators[sortKey];
    // Invert the comparator rather than sorting ascending then reversing -
    // reversing the whole array would also reverse the relative order of
    // tied rows, which a descending sort shouldn't do.
    const directionalComparator =
      sortDirection === "asc"
        ? comparator
        : (a: T, b: T) => -comparator(a, b);

    return filtered.sort(directionalComparator);
  }, [rows, filterText, sortKey, sortDirection, matchesFilter, comparators]);

  return { filterText, setFilterText, sortKey, sortDirection, toggleSort, visibleRows };
}
